<script setup lang="ts">
import { ref } from "vue"
import Section from "@/types/static"

const intro =
  "These XY Planning Network (XYPN) XY Archive Terms of Access are a legal agreement between XYPN (“XYPN” “we” or “us”) and you, the user of XY Archive (“you”). By accessing XY Archive (“the Service”), you agree to be bound by these terms and conditions (“Terms”)."
const sections = ref<Section[]>([
  {
    open: false,
    title: "1. General",
    paragraphs: [
      {
        content:
          "XY Archive is a technology that enables you, a registered investment advisor that is a member of XYPN, to securely archive communications made to your clients or about your investment advisory services (“your data”) via your own systems and outside providers such as social media sites, business networking sites, and other APIs (“Third-Party Providers”). The Service will copy, transmit, and reproduce the obtained and archived data to be accessible via the Service’s platform and available via a download when needed or necessitated to fulfill regulatory requirements. XY Archive is powered by AWS.",
        header: "",
      },
      {
        content:
          "“AWS” means Amazon Web Services Inc., 410 Terry Avenue North, Seattle, WA 98109-5210, USA, and its subsidiaries or affiliates.",
        header: "",
      },
    ],
  },
  {
    open: false,
    title: "2. Using XY Archive",
    paragraphs: [
      {
        header: "User Registration",
        content:
          "You are required to register with the Service to begin use. Upon your initial request to access the Service, you will have the choice to access the Service via authentication with Google or by creating an account with your email and password. We may issue certain identifying information (such as an account name or number and a password) based on the identifying information that you will provide us (such as your email address) (all together “XYPN Credentials”). It is your responsibility to ensure that your registration information is complete and accurate. You agree to keep your password confidential and will be responsible for all use of your account and password.",
      },
      {
        content:
          "You understand and agree that all XYPN Credentials are stored with AWS and that AWS may, at its discretion and from time to time, use affiliates and/or various third-party providers to store your XYPN Credentials.",
        header: "",
      },
      {
        content:
          "XYPN will request that AWS use reasonable efforts to keep your XYPN Credentials secure.",
        header: "",
      },
      {
        header: "Your Data",
        content:
          "You agree to grant the Service the limited ability to access, copy, download, and reproduce your data as necessary to provide or improve services to you.",
      },
      {
        content:
          "You will notify XYPN of any delivery failures or outages that could affect the successful archiving of your data. It is your responsibility to ensure your data is captured effectively.",
        header: "",
      },
      {
        content:
          "You, not the Service, are responsible for the content of your data. We make no effort to review any of your data, for reasons including but not limited to: accuracy, legality, or non-infringement needs.",
        header: "",
      },
      {
        header: "Your Account",
        content:
          "You are responsible for your account, including but not limited to the activity that occurs within the Service under your user credentials. You will alert XYPN of any unauthorized use of your account or breach of the security of your account.",
      },
      {
        header: "Agreements",
        content:
          "Your access to the Service shall be subject to the privacy policy for the Service available at archive.xyplanningnetwork.com/privacy-policy. You agree to the use of your data (including without limitation your XYPN Credentials) in accordance with XYPN’s privacy policies.",
      },
    ],
  },
  {
    open: false,
    title: "3. Our Role and Third-Party Providers",
    paragraphs: [
      {
        content:
          "As part of the functionality of the Service, you may link your account with online accounts you have with Third-Party Providers for the purpose of archiving the data and communications therein. The authorization of the Third-Party Provider will be done on their site and domain and with your own login credentials with that Third-Party Provider. The Service will not be made aware of or given your login credentials to any Third-Party Provider.",
        header: "",
      },
      {
        content:
          "You grant us access to your Third-Party Provider account without breach by you of any of the terms and conditions that govern your use of the applicable Third-Party Provider. By doing so, you grant the Third-Party Provider the ability and permission to transmit and make accessible data to XY Archive.",
        header: "",
      },
      {
        content:
          "Your relationship with any Third-Party Provider’s service is governed solely by your agreement(s) with those services. We make no effort to review any of your data for any purpose, including but not limited to reasons for accuracy, legality, or non-infringement. The Service does not offer, provide, or oversee the services of Third-Party Providers, and as such Third-Party Providers may update, modify, or terminate services to you without the Service’s notice or consent. XY Archive disclaims all liability from a Third-Party Provider’s service, including your use of the service, as well as updates, corruption, loss of data, or termination of your account on behalf of the Third-Party Provider.",
        header: "",
      },
      {
        content:
          "You will have the ability to disable the connection between the Service and Third-Party Provider accounts at any time.",
        header: "",
      },
      {
        content:
          "XYPN does not act as your agent or on your behalf. XYPN does not act as your RIA’s agent or on their behalf.",
        header: "",
      },
      {
        header: "YouTube",
        content:
          "We use YouTube API services in order to archive your YouTube data. If you choose to utilize our YouTube archiving services, you are agreeing to be bound by YouTube’s Terms of Service. Their Terms of Service can be found here: https://www.youtube.com/t/terms.",
      },
      {
        header: "Google",
        content:
          "If you choose to use one or all of the following services or features:",
        list: [
          "Google authentication for login",
          "Google email archiving",
          "YouTube archiving",
        ],
      },
      {
        content:
          "You are agreeing to be bound by Google’s Privacy Policy. Their Privacy Policy can be found here: https://policies.google.com/privacy.",
        header: "",
      },
      {
        content:
          "If you ever want to change your security settings with Google, you can manage those settings here: https://myaccount.google.com/permissions.",
        header: "",
      },
    ],
  },
  {
    open: false,
    title: "4. Ending Service",
    paragraphs: [
      {
        content:
          "If you want to delete your XY Archive Credentials or change your settings, you can send an email to archive@xyplanningnetwork.com.",
        header: "",
      },
    ],
  },
  {
    open: false,
    title: "5. Representations and Prohibited Activities",
    paragraphs: [
      {
        content:
          "By accessing XY Archive, you represent and warrant that you are at least 18 years of age and that you will not access or use XY Archive for any fraudulent, unlawful or abusive purpose.",
        header: "",
      },
      {
        content:
          "You may not access or use the Service for any purpose other than that for which we make the Service available.",
        header: "",
      },
      {
        content: "As a user of the Service, you agree not to:",
        header: "",
        list: [
          "Defraud or mislead us, especially in any attempt to learn or gain access to sensitive account information such as other user’s passwords or data. This includes attempting to impersonate another user or using the XYPN credentials of another user.",
          "Make improper use of our support services or submit false reports of abuse or misconduct.",
          "Interfere with, disrupt, or create an undue burden on the Service or the networks or servers connected to the Service.",
          "Decipher, decompile, disassemble, or reverse engineer any of the software comprising or in any way making up a part of XY Archive.",
        ],
      },
    ],
  },
  {
    open: false,
    title: "6. DISCLAIMERS",
    paragraphs: [
      {
        content:
          "XY ARCHIVE, INCLUDING ALL CONTENT, SOFTWARE, FUNCTIONS, MATERIALS, AND INFORMATION MADE AVAILABLE ON, PROVIDED IN CONNECTION WITH OR ACCESSIBLE THROUGH XY ARCHIVE, ARE PROVIDED “AS IS.” TO THE FULLEST EXTENT PERMISSIBLE BY LAW, XYPN, ITS AFFILIATES, AND THEIR AGENTS, MERCHANTS OR INDEPENDENT CONTRACTORS (THE “DISCLAIMING ENTITIES”), MAKE NO REPRESENTATION OR WARRANTY OF ANY KIND WHATSOEVER FOR THE CONTENT, MATERIALS, INFORMATION AND FUNCTIONS MADE ACCESSIBLE BY XY ARCHIVE, OR FOR ANY BREACH OF SECURITY ASSOCIATED WITH THE TRANSMISSION OF SENSITIVE INFORMATION YOU INPUT IN XY ARCHIVE. EACH DISCLAIMING ENTITY DISCLAIMS WITHOUT LIMITATION, ANY WARRANTY OF ANY KIND INCLUDING, WITHOUT LIMITATION, NONINFRINGEMENT, MERCHANTABILITY, OR FITNESS FOR A PARTICULAR PURPOSE. THE DISCLAIMING ENTITIES DO NOT WARRANT THAT THE FUNCTIONS CONTAINED IN XY ARCHIVE WILL BE UNINTERRUPTED OR ERROR FREE. THE DISCLAIMING ENTITIES SHALL NOT BE RESPONSIBLE FOR ANY UPTIME INTERRUPTIONS, INCLUDING, BUT NOT LIMITED TO, SYSTEM FAILURES OR OTHER INTERRUPTIONS THAT MAY AFFECT THE ARCHIVING, DELIVERY, AND ACCESSIBILITY OF ARCHIVED DATA.",
        header: "",
      },
    ],
  },
  {
    open: false,
    title: "7. Limitations of Liability; Force Majeure",
    paragraphs: [
      {
        content:
          "NOTWITHSTANDING ANY OTHER TERM, IN NO EVENT SHALL ANY DISCLAIMING ENTITY BE RESPONSIBLE OR LIABLE TO YOU OR ANY THIRD-PARTY UNDER ANY CIRCUMSTANCES FOR ANY INDIRECT, CONSEQUENTIAL, SPECIAL, PUNITIVE OR EXEMPLARY, DAMAGES OR LOSSES, INCLUDING BUT NOT LIMITED TO DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES WHICH MAY BE INCURRED IN CONNECTION WITH ANY DISCLAIMING ENTITY OR THE SERVICES, OR ANY INFORMATION ARCHIVED BY WAY OF THE SERVICE, REGARDLESS OF THE TYPE OF CLAIM OR THE NATURE OF THE CAUSE OF ACTION, EVEN IF THE DISCLAIMING ENTITY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGE OR LOSS. IN NO EVENT SHALL THE DISCLAIMING ENTITIES’ TOTAL CUMULATIVE LIABILITY ARISING FROM OR RELATING TO THESE TERMS OF SERVICE EXCEED $3 MILLION US DOLLARS.",
        header: "",
      },
      {
        content:
          "Each party acknowledges that the other party has entered into these Terms relying on the limitations of liability stated herein and that those limitations are an essential basis of the bargain between the parties. In addition to and without limiting any of the foregoing, no Disclaiming Entity shall have any liability for any failure or delay resulting from any condition beyond the reasonable control of such party and even if foreseeable, including but not limited to governmental action or acts of terrorism, earthquake, fire, flood or other acts of God, labor conditions, power failures and Internet disturbances.",
        header: "",
      },
    ],
  },
  {
    open: false,
    title: "8. Governing Law",
    paragraphs: [
      {
        content:
          "These Terms of Service will be governed by the laws of Montana, except for Montana’s choice of law rules, and applicable federal United States laws. Each party agrees to submit to personal and exclusive jurisdiction of the courts located in Gallatin County, Montana.",
        header: "",
      },
    ],
  },
  {
    open: false,
    title: "9. Notice",
    paragraphs: [
      {
        content:
          "Notices and other communications to you may be made by mail, email, postings on XY Archive or other reasonable means. We may also provide notices of changes to the Terms or other matters by displaying links to notices on the XYPN website or at archive.xyplanningnetwork.com. Notice to XYPN may be made to XY Planning Network, 24 E Main St., Bozeman, Montana, 59715, USA.",
        header: "",
      },
    ],
  },
  {
    open: false,
    title: "10. Modification of Terms of Service",
    paragraphs: [
      {
        content:
          "We have the right, in our sole and absolute discretion, to change, modify, or amend any portion of these Terms at any time by posting notification here or otherwise communicating the notification to you. The changes will become effective, and shall be deemed accepted by you, after the initial posting and shall apply on a going-forward basis. In the event that you do not agree with any such modification, your sole and exclusive remedy is to terminate your use of the Service.",
        header: "",
      },
    ],
  },
  {
    open: false,
    title: "11. Termination / Assignment",
    paragraphs: [
      {
        content:
          "XYPN shall have the right to terminate your use of the Service at its entire discretion, at any time and without notice. Such termination shall not give rise to any liability for XYPN or obligation to pay any amount by XYPN.",
        header: "",
      },
      {
        content:
          "Termination of use of XY Archive will occur automatically if you end your membership with XYPN. Your data will be available to you via download in its entirety to use with another service in the future.",
        header: "",
      },
      {
        content:
          "You may not assign these Terms or the whole or part of any rights or obligations hereunder, by operation of law or otherwise, without our prior written approval and any such attempted assignment shall be void. We reserve the right to freely assign these Terms and the rights and obligations hereunder, to any third-party without notice or consent. Subject to the foregoing, these Terms of Service shall be binding upon and inure to the benefit of the parties hereto, their successors and permitted assigns.",
        header: "",
      },
    ],
  },
  {
    open: false,
    title: "12. Survival",
    paragraphs: [
      {
        content:
          "Upon termination of your use of XY Archive, or termination of these Terms for any reason, in addition to this section, the following sections shall survive termination: Sections 5 through 11.",
        header: "",
      },
    ],
  },
  {
    open: false,
    title: "13. Miscellaneous",
    paragraphs: [
      {
        content:
          "XYPN’s failure to exercise or enforce any right or provision of the Terms will not be considered a waiver of that right or provision. If any provision of these Terms shall be adjudged by any court of competent jurisdiction to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect and remain enforceable between the parties. Headings are for reference purposes only and in no way define, limit, construe or describe the scope or extent of such section. These Terms, including XYPN’s policies referenced herein, constitute the entire agreement between you and XYPN with respect to the use of XY Archive. These Terms are not intended and shall not be construed to create any rights or remedies in any parties other than you and XYPN and no other person will have the ability to assert any rights as a third-party beneficiary under these Terms of Service.",
        header: "",
      },
    ],
  },
])
</script>

<template>
  <div class="bg-gray-50">
    <div class="max-w-screen-xl mx-auto py-12 px-4 sm:py-16 sm:px-6 lg:px-8">
      <div class="max-w-3xl mx-auto">
        <h2
          class="text-center text-3xl leading-9 font-extrabold text-gray-900 sm:text-4xl sm:leading-10"
        >
          Terms and Conditions
        </h2>
        <h3
          class="mt-1 text-center text-lg leading-4 text-gray-600 sm:text-xl sm:leading-5"
        >
          XY Archive Terms of Access<br />
          Last Revised: 1/19/21
        </h3>
        <p class="text-sm leading-6 text-gray-600 mt-6" v-text="intro"></p>
        <div class="mt-6 border-t-2 border-gray-200 pt-6">
          <dl>
            <div
              v-for="(section, idx) in sections"
              :key="idx"
              :class="{ 'mt-6 border-t border-gray-200 pt-6': idx > 0 }"
            >
              <dt class="text-lg leading-7">
                <button
                  class="text-left w-full flex justify-between items-start text-gray-400 focus:outline-none focus:text-gray-900"
                  @click="section.open = !section.open"
                >
                  <span class="font-medium text-gray-900">
                    {{ section.title }}
                  </span>
                  <span class="ml-6 h-7 flex items-center">
                    <svg
                      class="h-6 w-6 transform"
                      :class="{ '-rotate-180': section.open }"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </span>
                </button>
              </dt>
              <dd
                v-for="(paragraph, pidx) in section.paragraphs"
                v-show="section.open"
                :key="idx - pidx"
                class="mt-2 pr-12"
              >
                <h4 v-if="!!paragraph.header" v-text="paragraph.header"></h4>
                <p
                  class="text-base leading-6 text-gray-600"
                  v-text="paragraph.content"
                ></p>
                <ol v-if="!!paragraph.list" class="list-decimal mt-1">
                  <li
                    v-for="(l, lidx) in paragraph.list"
                    :key="idx - pidx - lidx"
                    class="text-sm leading-6 text-gray-600"
                    v-text="l"
                  ></li>
                </ol>
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>

    <div class="border-t py-12 px-4 sm:py-16 sm:px-6 lg:px-8">
      <h2
        class="text-2xl leading-8 font-extrabold text-gray-900 sm:text-3xl sm:leading-9"
      >
        Contact Us
      </h2>
      <div class="mt-3">
        <p class="text-lg leading-7 text-gray-600">
          In order to resolve a complaint regarding XY Archive or to receive
          further information regarding the use of XY Archive, please contact us
          at:
        </p>
      </div>
      <div class="mt-9">
        <div class="flex">
          <div class="shrink-0">
            <svg
              class="h-6 w-6 text-gray-400"
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              ></path>
            </svg>
          </div>
          <div class="ml-3 text-base leading-6 text-gray-600">
            <p>XYPN</p>
            <p>24 E Main St</p>
            <p>Bozeman, MT 59715</p>
            <p>United States</p>
            <p>Phone: 406-413-9200</p>
          </div>
        </div>
        <div class="mt-6 flex">
          <div class="shrink-0">
            <svg
              class="h-6 w-6 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              ></path>
            </svg>
          </div>
          <div class="ml-3 text-base leading-6 text-gray-600">
            <p>archive@xyplanningnetwork.com</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
